/**
 * Iter v31 — Face-api loader using @vladmandic/face-api npm (more reliable than CDN).
 * Models served same-origin from /public/models/ (Vladmandic .bin format).
 */
import * as faceapi from "@vladmandic/face-api";

const MODELS_PATH = `${process.env.PUBLIC_URL || ""}/models`;

let _loadingPromise = null;

// Iter v34al — Detect low-end devices (HP 4GB RAM, 4 core, atau kurang) untuk
// menurunkan resolusi kamera & input model face-api. Reduces CPU/RAM pressure
// dan mencegah lag/freeze pada perangkat kelas bawah.
export const IS_LOW_END = (() => {
  if (typeof navigator === "undefined") return false;
  const mem = navigator.deviceMemory || 8;      // undefined di iOS → assume high
  const cores = navigator.hardwareConcurrency || 8;
  return mem <= 4 || cores <= 4;
})();

export async function loadFaceApi(onProgress) {
  if (typeof window === "undefined") throw new Error("Tidak dapat load face-api pada SSR");
  if (faceapi.nets.tinyFaceDetector.isLoaded && faceapi.nets.faceLandmark68Net.isLoaded && faceapi.nets.faceRecognitionNet.isLoaded) {
    return faceapi;
  }
  if (_loadingPromise) return _loadingPromise;

  _loadingPromise = (async () => {
    onProgress && onProgress("Menginisialisasi engine…");
    // Ensure a backend is ready (WebGL preferred, CPU fallback). Without this
    // step, the very first face-api call may fail with "highest priority backend
    // has not been initialized" on some browsers (notably headless Chrome).
    try {
      await faceapi.tf.ready();
      const current = faceapi.tf.getBackend();
      if (!current || current === "wasm") {
        // Fall through to CPU if WebGL not available
        try { await faceapi.tf.setBackend("webgl"); } catch { /* keep CPU */ }
        if (!faceapi.tf.getBackend()) await faceapi.tf.setBackend("cpu");
        await faceapi.tf.ready();
      }
    } catch (e) {
      // If everything fails, force CPU
      try { await faceapi.tf.setBackend("cpu"); await faceapi.tf.ready(); } catch { /* */ }
    }
    // Iter v34aq — Parallel model loading (was sequential). All 3 models
    // needed together; loading concurrently reduces total wall time by ~60%
    // on typical 3-4G mobile connections (network is the bottleneck, not CPU).
    onProgress && onProgress("Memuat model wajah…");
    await Promise.all([
      faceapi.nets.tinyFaceDetector.isLoaded ? null : faceapi.nets.tinyFaceDetector.loadFromUri(MODELS_PATH),
      faceapi.nets.faceLandmark68Net.isLoaded ? null : faceapi.nets.faceLandmark68Net.loadFromUri(MODELS_PATH),
      faceapi.nets.faceRecognitionNet.isLoaded ? null : faceapi.nets.faceRecognitionNet.loadFromUri(MODELS_PATH),
    ].filter(Boolean));

    // Iter v34aq — WARMUP: run one real inference on a small offscreen canvas
    // so the model kernels are JIT-compiled AND weights uploaded to WebGL.
    // Cuts first-detection latency by ~600-1500ms on mobile. Best-effort.
    try {
      const off = typeof document !== "undefined" ? document.createElement("canvas") : null;
      if (off) {
        off.width = 128;
        off.height = 128;
        const ctx = off.getContext("2d");
        // Fill with a neutral gradient so the detector has enough signal to
        // execute the full pipeline (returns null → but compiles the graph).
        const g = ctx.createLinearGradient(0, 0, 128, 128);
        g.addColorStop(0, "#c0c0c0"); g.addColorStop(1, "#606060");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, 128, 128);
        await faceapi.detectSingleFace(
          off,
          new faceapi.TinyFaceDetectorOptions({ inputSize: 128, scoreThreshold: 0.5 }),
        );
      }
    } catch { /* warmup best-effort — never fail user-facing load */ }

    onProgress && onProgress("Siap.");
    // Expose for debugging
    window.faceapi = faceapi;
    return faceapi;
  })();
  try {
    return await _loadingPromise;
  } catch (e) {
    _loadingPromise = null; // allow retry
    throw e;
  }
}

// Iter v34aq — Public helper for OTHER routes to warm face-api in background
// without blocking. Call it from top-level auth-guard or app shell so models
// are already loaded when karyawan navigates to /karyawan/absen.
export function preloadFaceApi() {
  if (typeof window === "undefined") return;
  // Fire and forget — do NOT await. Errors ignored (retry on real use).
  loadFaceApi(() => {}).catch(() => {});
}

/**
 * Mobile-friendly camera initializer (iOS Safari, Android Chrome, desktop).
 */
export async function getCamera(videoEl, { facingMode = "user", width, height } = {}) {
  // Iter v34al — Lower camera resolution on low-end devices to reduce
  // GPU/CPU load. 480x360 is sufficient for TinyFaceDetector 192.
  if (width == null) width = IS_LOW_END ? 480 : 640;
  if (height == null) height = IS_LOW_END ? 360 : 480;
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("Browser tidak mendukung akses kamera. Gunakan Chrome/Safari versi terbaru di atas HTTPS.");
  }
  const constraints = {
    audio: false,
    video: {
      facingMode: { ideal: facingMode },
      width: { ideal: width },
      height: { ideal: height },
    },
  };
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia(constraints);
  } catch (err) {
    if (err?.name === "OverconstrainedError" || err?.name === "ConstraintNotSatisfiedError") {
      stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    } else if (err?.name === "NotAllowedError") {
      throw new Error("Izin kamera ditolak. Aktifkan di pengaturan browser.");
    } else if (err?.name === "NotFoundError") {
      throw new Error("Tidak ada kamera terdeteksi di perangkat ini.");
    } else if (err?.name === "NotReadableError") {
      throw new Error("Kamera sedang digunakan aplikasi lain.");
    } else {
      throw new Error("Gagal akses kamera: " + (err?.message || err));
    }
  }
  if (videoEl) {
    videoEl.setAttribute("playsinline", "true");
    videoEl.setAttribute("autoplay", "true");
    videoEl.setAttribute("muted", "true");
    videoEl.muted = true;
    videoEl.srcObject = stream;
    await new Promise((resolve) => {
      if (videoEl.readyState >= 2) return resolve();
      const onReady = () => { videoEl.removeEventListener("loadedmetadata", onReady); resolve(); };
      videoEl.addEventListener("loadedmetadata", onReady, { once: true });
    });
    try { await videoEl.play(); } catch { /* iOS autoplay block may still resolve via muted */ }
    // Iter v34g — Poll videoWidth up to 3s: iOS Safari sometimes resolves play()
    // before frames actually flow. If still 0, throw an actionable error.
    let waited = 0;
    while ((!videoEl.videoWidth || videoEl.videoWidth === 0) && waited < 3000) {
      await new Promise((r) => setTimeout(r, 100));
      waited += 100;
      // try play() again — iOS sometimes needs a nudge after attaching srcObject
      try { await videoEl.play(); } catch { /* ignore */ }
    }
    if (!videoEl.videoWidth) {
      stream.getTracks().forEach((t) => t.stop());
      throw new Error("Kamera tidak menampilkan gambar. Cek izin kamera di pengaturan browser & coba refresh halaman.");
    }
  }
  return stream;
}

export function stopStream(stream) {
  if (!stream) return;
  try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
}

export async function detectFace(videoEl) {
  if (!videoEl || !videoEl.videoWidth) throw new Error("Kamera belum siap");
  // Iter v34al — Lower inputSize for low-end devices (RAM/CPU savings).
  const detectSize = IS_LOW_END ? 224 : 320;
  const det = await faceapi
    .detectSingleFace(videoEl, new faceapi.TinyFaceDetectorOptions({ inputSize: detectSize, scoreThreshold: 0.5 }))
    .withFaceLandmarks()
    .withFaceDescriptor();
  if (!det) return null;
  return { descriptor: Array.from(det.descriptor), box: det.detection.box };
}

/**
 * Iter v32 — Liveness check: deteksi 1x kedip mata dalam <timeoutMs> ms.
 * Algoritma: hitung Eye Aspect Ratio (EAR) tiap frame. Jika EAR turun di
 * bawah CLOSED_THRESHOLD lalu naik di atas OPEN_THRESHOLD → 1 kedip.
 * Reference: Soukupová & Čech (2016).
 */
function _eyeAR(landmarks, eyeIndices) {
  // landmarks: array of 68 points, eyeIndices: 6 indices [outer, top1, top2, inner, bottom2, bottom1]
  const p = eyeIndices.map((i) => landmarks[i]);
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const vertical = (dist(p[1], p[5]) + dist(p[2], p[4])) / 2;
  const horizontal = dist(p[0], p[3]);
  return horizontal > 0 ? vertical / horizontal : 0;
}

// 68-point landmark indices (dlib standard) for left/right eye
const LEFT_EYE = [36, 37, 38, 39, 40, 41];
const RIGHT_EYE = [42, 43, 44, 45, 46, 47];

export async function detectBlink(videoEl, { timeoutMs, onTick } = {}) {
  if (!videoEl || !videoEl.videoWidth) throw new Error("Kamera belum siap");
  // Iter v34ap — Longer default timeout (was 5000). Users need time to blink
  // naturally after seeing the prompt. Low-end devices: extra 2s for slower
  // frame rate.
  if (timeoutMs == null) timeoutMs = IS_LOW_END ? 9000 : 7000;
  const start = Date.now();

  // Iter v34ap — Adaptive baseline calibration. Some users (esp. with
  // naturally narrow eyes / asian eyes) have baseline EAR of only 0.20-0.24
  // which sits BELOW the old absolute CLOSED=0.25 threshold. That made the
  // liveness algorithm never see them "open" and always fail. We now sample
  // the user's own resting EAR for the first ~800ms and derive CLOSED as a
  // RELATIVE drop from their baseline.
  const calibrateMs = 800;
  const calibrationSamples = [];

  let state = "unknown";
  let blinks = 0;
  let minEar = 1;
  let maxEar = 0;
  let baseline = null;
  let closedThreshold = null;
  let openThreshold = null;

  while (Date.now() - start < timeoutMs) {
    try {
      const det = await faceapi
        .detectSingleFace(videoEl, new faceapi.TinyFaceDetectorOptions({ inputSize: IS_LOW_END ? 160 : 224, scoreThreshold: 0.4 }))
        .withFaceLandmarks();
      if (det) {
        const pts = det.landmarks.positions;
        const leftEAR = _eyeAR(pts, LEFT_EYE);
        const rightEAR = _eyeAR(pts, RIGHT_EYE);
        const ear = (leftEAR + rightEAR) / 2;
        if (ear < minEar) minEar = ear;
        if (ear > maxEar) maxEar = ear;

        const elapsed = Date.now() - start;

        // Calibration phase — collect samples of resting EAR.
        if (elapsed < calibrateMs) {
          calibrationSamples.push(ear);
        } else if (baseline == null) {
          // Sort ascending, take median of top-half (assume open) as baseline.
          const sorted = [...calibrationSamples].sort((a, b) => a - b);
          const topHalf = sorted.slice(Math.floor(sorted.length / 2));
          baseline = topHalf.length ? topHalf[Math.floor(topHalf.length / 2)] : Math.max(0.20, maxEar);
          // A blink drops EAR ~30-40% from baseline. Set thresholds relatively.
          closedThreshold = Math.max(0.14, baseline * 0.75);
          openThreshold = Math.max(0.16, baseline * 0.85);
          state = "open";
        }

        onTick && onTick({
          ear: +ear.toFixed(3), state, blinks, elapsed,
          minEar: +minEar.toFixed(3), maxEar: +maxEar.toFixed(3),
          baseline: baseline != null ? +baseline.toFixed(3) : null,
          closedTh: closedThreshold != null ? +closedThreshold.toFixed(3) : null,
        });

        // Blink detection (post-calibration).
        if (baseline != null) {
          if (state === "open" && ear < closedThreshold) state = "closed";
          else if (state === "closed" && ear > openThreshold) {
            state = "open";
            blinks += 1;
            if (blinks >= 1) return {
              ok: true, blinks, durationMs: Date.now() - start,
              baseline, minEar, maxEar,
            };
          }
        }
      } else {
        onTick && onTick({ ear: 0, state, blinks, elapsed: Date.now() - start, noFace: true });
      }
    } catch { /* ignore frame error */ }
    // Iter v34al — Longer frame delay on low-end devices (mengurangi beban CPU).
    await new Promise((r) => setTimeout(r, IS_LOW_END ? 90 : 50));
  }
  // Iter v34ap — Robust fallback: accept if EAR dynamic range shows a clear
  // dip regardless of absolute values. Threshold lowered to 0.02 (was 0.03)
  // because narrow-eye subjects have less absolute movement.
  if (maxEar - minEar >= 0.02) {
    return {
      ok: true, blinks: Math.max(blinks, 1),
      durationMs: Date.now() - start, fallback: true,
      minEar, maxEar, baseline,
    };
  }
  return {
    ok: false, blinks,
    durationMs: Date.now() - start,
    minEar, maxEar, baseline,
    // Diagnostic hint for the UI to surface actionable feedback.
    reason: baseline == null ? "no_face" : (maxEar - minEar < 0.01 ? "no_movement" : "shallow_blink"),
  };
}

export function snapshotVideo(videoEl, canvasEl, quality = 0.7) {
  if (!videoEl?.videoWidth) return "";
  const cv = canvasEl || document.createElement("canvas");
  cv.width = videoEl.videoWidth;
  cv.height = videoEl.videoHeight;
  cv.getContext("2d").drawImage(videoEl, 0, 0);
  return cv.toDataURL("image/jpeg", quality).split(",")[1] || "";
}
